import { API_CONFIG } from './config';
import { AppError, type AppErrorKind } from './errors';

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  body?: unknown;
  signal?: AbortSignal;
  timeoutMs?: number;
}

const TIMEOUT_ERROR_NAME = 'TimeoutError';

interface ErrorResponseBody {
  message?: string;
  detail?: string;
}

const SUBSCRIPTION_REQUIRED_DETAIL = 'provider subscription is not active.';

const isSubscriptionRequired = (status: number, body: ErrorResponseBody | null): boolean =>
  status === 402 ||
  (status === 403 && body?.detail?.trim().toLowerCase() === SUBSCRIPTION_REQUIRED_DETAIL);

const statusToKind = (status: number, body: ErrorResponseBody | null): AppErrorKind => {
  if (isSubscriptionRequired(status, body)) return 'paymentRequired';
  if (status === 401 || status === 403) return 'auth';
  if (status >= 400 && status < 500) return 'client';
  if (status >= 500) return 'server';
  return 'unknown';
};

class HttpClient {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;
  private token: string | null = null;
  private refreshPromise: Promise<string> | null = null;
  private onUnauthorized?: () => Promise<string>;

  constructor(baseURL: string) {
    this.baseURL = baseURL.replace(/\/$/, '');
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
  }

  setToken(token: string | null) {
    this.token = token;
  }

  setUnauthorizedHandler(handler: () => Promise<string>) {
    this.onUnauthorized = handler;
  }

  private async makeRequest<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { method = 'GET', headers = {}, body, signal, timeoutMs } = options;

    const url = `${this.baseURL}${endpoint}`;

    const requestHeaders: Record<string, string> = {
      ...this.defaultHeaders,
      ...headers,
    };

    const isFormData = body instanceof FormData;
    if (isFormData) {
      delete requestHeaders['Content-Type'];
    }

    if (this.token) {
      requestHeaders.Authorization = `Bearer ${this.token}`;
    }

    const config: RequestInit = {
      method,
      headers: requestHeaders,
      signal,
    };

    if (body !== undefined && method !== 'GET') {
      config.body = isFormData ? body : JSON.stringify(body);
    }

    try {
      const response = await this.fetchWithTimeout(url, config, timeoutMs);

      if (!response.ok) {
        if (
          response.status === 401 &&
          this.onUnauthorized &&
          !endpoint.includes('/auth/telegram/exchange')
        ) {
          const newToken = await this.refreshAccessToken();

          requestHeaders.Authorization = `Bearer ${newToken}`;
          const retryConfig: RequestInit = { ...config, headers: requestHeaders };
          const retryResponse = await this.fetchWithTimeout(url, retryConfig, timeoutMs);
          return this.handleResponse<T>(retryResponse);
        }

        return this.handleResponse<T>(response);
      }

      return this.parseResponse<T>(response);
    } catch (error) {
      throw this.toAppError(error, signal);
    }
  }

  private toAppError(error: unknown, callerSignal: AbortSignal | undefined): AppError {
    if (error instanceof AppError) return error;

    if (this.isTimeoutError(error)) {
      return new AppError('Request timed out', 'timeout', undefined, undefined, error);
    }

    if (callerSignal?.aborted) {
      return new AppError('Request was cancelled', 'cancelled', undefined, undefined, error);
    }

    if (error instanceof TypeError) {
      return new AppError(
        'Failed to connect to server. Please check your connection.',
        'network',
        undefined,
        undefined,
        error
      );
    }

    const message = error instanceof Error ? error.message : 'Unknown error';
    return new AppError(message, 'unknown', undefined, undefined, error);
  }

  private isTimeoutError(error: unknown): boolean {
    if (error instanceof DOMException && error.name === TIMEOUT_ERROR_NAME) return true;
    if (error instanceof Error && error.name === TIMEOUT_ERROR_NAME) return true;
    return false;
  }

  private async refreshAccessToken(): Promise<string> {
    if (!this.onUnauthorized) {
      throw new AppError('Unauthorized', 'auth', 401);
    }

    if (!this.refreshPromise) {
      this.refreshPromise = this.onUnauthorized()
        .then((token) => {
          this.token = token;
          return token;
        })
        .catch((error) => {
          this.token = null;
          throw error;
        })
        .finally(() => {
          this.refreshPromise = null;
        });
    }

    return this.refreshPromise;
  }

  private async fetchWithTimeout(
    url: string,
    config: RequestInit,
    timeoutMs?: number
  ): Promise<Response> {
    const timeout = timeoutMs ?? API_CONFIG.timeout;
    if (!timeout || timeout <= 0) {
      return fetch(url, config);
    }

    const controller = new AbortController();
    const timeoutError = new DOMException('Request timed out', TIMEOUT_ERROR_NAME);
    const timeoutId = setTimeout(() => controller.abort(timeoutError), timeout);
    const onAbort = () => controller.abort((config.signal as AbortSignal | undefined)?.reason);

    if (config.signal) {
      if (config.signal.aborted) {
        controller.abort(config.signal.reason);
      } else {
        config.signal.addEventListener('abort', onAbort);
      }
    }

    try {
      return await fetch(url, { ...config, signal: controller.signal });
    } catch (error) {
      if (controller.signal.aborted) {
        const reason: unknown = controller.signal.reason;
        if (reason instanceof DOMException && reason.name === TIMEOUT_ERROR_NAME) {
          throw reason;
        }
        if (reason instanceof Error) throw reason;
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
      if (config.signal) {
        config.signal.removeEventListener('abort', onAbort);
      }
    }
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (response.ok) {
      return this.parseResponse<T>(response);
    }

    const errorData = await this.tryParseJson(response);
    const errorBody = errorData as ErrorResponseBody | null;
    const message =
      errorBody?.message || errorBody?.detail || `HTTP ${response.status}: ${response.statusText}`;

    throw new AppError(
      message,
      statusToKind(response.status, errorBody),
      response.status,
      errorData
    );
  }

  private async tryParseJson(response: Response): Promise<unknown> {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  private async parseResponse<T>(response: Response): Promise<T> {
    if (response.status === 204) {
      return undefined as T;
    }

    const contentType = response.headers.get('content-type') ?? '';
    const text = await response.text();

    if (!text) {
      return undefined as T;
    }

    if (contentType.includes('application/json')) {
      return JSON.parse(text) as T;
    }

    try {
      return JSON.parse(text) as T;
    } catch {
      return text as unknown as T;
    }
  }

  async get<T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.makeRequest<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(
    endpoint: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>
  ): Promise<T> {
    return this.makeRequest<T>(endpoint, { ...options, method: 'POST', body });
  }

  async put<T>(
    endpoint: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>
  ): Promise<T> {
    return this.makeRequest<T>(endpoint, { ...options, method: 'PUT', body });
  }

  async delete<T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.makeRequest<T>(endpoint, { ...options, method: 'DELETE' });
  }

  async patch<T>(
    endpoint: string,
    body?: unknown,
    options?: Omit<RequestOptions, 'method' | 'body'>
  ): Promise<T> {
    return this.makeRequest<T>(endpoint, { ...options, method: 'PATCH', body });
  }
}

export const httpClient = new HttpClient(API_CONFIG.baseURL);
